/** @jsx isolateComponent */
import { Fragment } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Button,
  Colors,
  Heading,
  Icon,
  Spacer,
  TextInput,
  Typography,
} from '@zapier/design-system';

import DocLinks from '../../shared/DocLinks';
import Section from '../../shared/Section';
import Surface from '../../shared/Surface';
import { SizeType } from '../../../types';

const subheadingCss = css`
  ${Typography.paragraph1Bold}
  color: ${Colors.neutral800};
`;

const DOC_LINKS = [
  { title: 'Create Zaps', href: `/help/create/basics/create-zaps` },
  {
    title: 'Learn key concepts in Zapier',
    href: `/help/create/basics/learn-key-concepts-in-zapier`,
  },
  {
    title: 'Understand tasks in Zapier',
    href: `/help/manage/tasks/understand-tasks-in-zapier`,
  },
  {
    title: 'Understand your Zapier premium trial',
    href: `/help/account/billing-pricing/understand-your-free-zapier-trial`,
  },
  {
    title: 'Troubleshoot error codes in Zaps',
    href: `/help/troubleshoot/errors/troubleshoot-error-codes-in-zaps`,
  },
];

type AdditionalHelpProps = {
  /** Custom title to use. */
  title?: string;
  /** Set to `true` to not promote Community. */
  disableCommunity?: boolean;
  /** Size to display. */
  size?: SizeType;
};

/**
 * Renders additional help dislayed in the footer.
 */
const AdditionalHelp = ({
  size = 'large',
  disableCommunity = false,
  title = 'Additional Help',
}: AdditionalHelpProps) => {
  return (
    <Section size={'small'} title={title}>
      <Spacer height={30} />
      {!disableCommunity && (
        <Fragment>
          <Surface size={size} title="Zapier Community">
            <p>
              Our Community is full of Zapier experts and moderators who can
              escalate questions to our Support team. Take a look at some of the
              conversations people have been having about questions like yours.
            </p>
            <Spacer height={20} />
            <Button
              color="secondary"
              href="https://community.zapier.com"
              size="small"
              target="_blank"
            >
              Browse Community
            </Button>
          </Surface>
          <Spacer height={40} />
        </Fragment>
      )}
      <Fragment>
        <Surface size={size} title="Hire an Expert">
          <p>
            We have a full directory of Zapier experts and agencies who you can
            hire to grow your business, implement your workflows and systems,
            and help with all of your marketing needs. Browse our directory, or
            submit a request for a quote.
          </p>
          <Spacer height={20} />
          <Button
            color="secondary"
            href="https://zapier.com/experts?utm_source=contact_page"
            size="small"
            target="_blank"
          >
            View our experts
          </Button>
        </Surface>
        <Spacer height={40} />
      </Fragment>
      <Surface size={size} title="Help Center">
        <p>
          From definitions to tutorials, find what you need to get your Zaps
          running.
        </p>
        <Spacer height={30} />
        <form
          action="https://zapier.com/help/search"
          method="GET"
          target="_blank"
        >
          <TextInput
            name="q"
            placeholder="Search the Help Center"
            renderIconBefore={({ iconSize }: { iconSize: number }) => (
              <Icon name="magnifyingGlass" size={iconSize} />
            )}
            size={'small'}
          />
        </form>
        <Spacer height={30} />
        <Heading css={subheadingCss}>Popular Help Articles</Heading>
        <Spacer height={10} />
        <DocLinks links={DOC_LINKS} size={size} />
        <Spacer height={30} />
        <Button
          color="secondary"
          href="https://zapier.com/help"
          size="small"
          target="_blank"
        >
          {'Browse articles'}
        </Button>
      </Surface>
    </Section>
  );
};

export default AdditionalHelp;
